import { createApi } from '@reduxjs/toolkit/query/react';
import { portalBaseQuery } from '@lib/baseQuery';
import { services } from '@config/services';
import type {
  AllRunsParams,
  Column,
  ColumnsResponse,
  DataRefreshedAtResponse,
  Export,
  ReportListParams,
  ReportListResponse,
  ReportRunsParams,
  PreviewBody,
  PreviewResponse,
  ReportWriteBody,
  Run,
  RunDetailParams,
  RunListResponse,
  RunReportArgs,
  ScopeKind,
} from './types';

/**
 * Append a query param only when it carries a value, encoding both key and
 * value. Mirrors `buildOrderListQuery` in `features/orders/api.ts` so the
 * Insight-service contract is built one param at a time (no array spread, no
 * implicit blanks) and reads identically to the rest of the codebase.
 */
function appendParam(
  parts: string[],
  key: string,
  value: string | number | boolean | null | undefined,
): void {
  if (value === null || value === undefined || value === '') return;
  parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
}

/**
 * Append the entity scope param. Confirmed from the legacy axios interceptor
 * (`injectEntityOverride: true`, default keys, scalar value) + live capture:
 * merchant scope sends `&merchantId=<id>`, vendor sends `&vendorId=<id>`,
 * admin scope sends NOTHING (the only auth header is the bearer token).
 */
function appendScope(
  parts: string[],
  scope: { merchantId?: string; vendorId?: string },
): void {
  appendParam(parts, 'merchantId', scope.merchantId);
  appendParam(parts, 'vendorId', scope.vendorId);
}

// --- URL builders (exported so the wire-shape lock can assert them as pure
//     functions — no fetch, no environment dependency). Each returns the exact
//     path + query string the live capture recorded. ----------------------

/** #1 `GET /insight/v1/reports?page&pageSize[&q][&merchantId|&vendorId]` */
export function buildReportsUrl({ page, pageSize, q, merchantId, vendorId }: ReportListParams): string {
  const parts: string[] = [];
  appendParam(parts, 'page', page);
  appendParam(parts, 'pageSize', pageSize);
  appendParam(parts, 'q', q);
  appendScope(parts, { merchantId, vendorId });
  return `${services.insight}/v1/reports?${parts.join('&')}`;
}

/** #3 `GET /insight/v1/reports/{id}/runs?page&pageSize[&merchantId|&vendorId]` */
export function buildReportRunsUrl({
  reportId,
  page,
  pageSize,
  merchantId,
  vendorId,
}: ReportRunsParams): string {
  const parts: string[] = [];
  appendParam(parts, 'page', page);
  appendParam(parts, 'pageSize', pageSize);
  appendScope(parts, { merchantId, vendorId });
  return `${services.insight}/v1/reports/${encodeURIComponent(reportId)}/runs?${parts.join('&')}`;
}

/** #5 `GET /insight/v1/reports/runs?page&pageSize&sortBy[&sortAsc][&scope]` */
export function buildAllRunsUrl({
  page,
  pageSize,
  sortBy,
  sortAsc,
  merchantId,
  vendorId,
}: AllRunsParams): string {
  const parts: string[] = [];
  appendParam(parts, 'page', page);
  appendParam(parts, 'pageSize', pageSize);
  appendParam(parts, 'sortBy', sortBy);
  // Matches legacy `getAllExportHistory`: omit `sortAsc` unless truthy.
  if (sortAsc) appendParam(parts, 'sortAsc', sortAsc);
  appendScope(parts, { merchantId, vendorId });
  return `${services.insight}/v1/reports/runs?${parts.join('&')}`;
}

/** #12 `DELETE /insight/v1/reports/{id}` (no scope param). */
export function buildDeleteReportUrl(reportId: string): string {
  return `${services.insight}/v1/reports/${encodeURIComponent(reportId)}`;
}

/** #2 `GET /insight/v1/reports/{id}` (builder edit mount; no scope param). */
export function buildReportUrl(reportId: string): string {
  return `${services.insight}/v1/reports/${encodeURIComponent(reportId)}`;
}

/** #6 `GET /insight/v1/reports/columns?runAs={runAs}` (builder mount). */
export function buildColumnsUrl(runAs: ScopeKind): string {
  return `${services.insight}/v1/reports/columns?runAs=${encodeURIComponent(runAs)}`;
}

/** #7 `GET /insight/v1/reports/{id}/runs/{runId}` (run-status polling; no scope). */
export function buildRunDetailUrl({ reportId, runId }: RunDetailParams): string {
  return `${services.insight}/v1/reports/${encodeURIComponent(reportId)}/runs/${encodeURIComponent(runId)}`;
}

/** #10 `POST /insight/v1/reports/{id}/run`. */
export function buildRunReportUrl(reportId: string): string {
  return `${services.insight}/v1/reports/${encodeURIComponent(reportId)}/run`;
}

/** #11 `POST /insight/v1/reports/preview`. */
export function buildPreviewUrl(): string {
  return `${services.insight}/v1/reports/preview`;
}

/** `GET /insight/v1/reports/data-refreshed-at` (warehouse freshness; no scope param). */
export function buildDataRefreshedAtUrl(): string {
  return `${services.insight}/v1/reports/data-refreshed-at`;
}

export const exportsApi = createApi({
  reducerPath: 'exportsApi',
  baseQuery: portalBaseQuery,
  tagTypes: ['Report', 'ReportList', 'Run', 'RunList'],
  endpoints: (builder) => ({
    /**
     * LIVE: GET /insight/v1/reports?page=1&pageSize=10[&q=<text>][&merchantId=<id>]
     * Response top-level keys observed live: { reports: Export[], total: number }.
     * (#1 in docs/migration/exports/link-graph.md)
     */
    getReports: builder.query<ReportListResponse, ReportListParams>({
      query: (params) => ({ url: buildReportsUrl(params), method: 'GET' }),
      // Wire already returns the envelope; the explicit pass-through documents
      // the observed `{ reports, total }` shape for the next reader.
      transformResponse: (response: ReportListResponse): ReportListResponse => ({
        reports: response.reports ?? [],
        total: response.total ?? 0,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.reports.map((r) => ({ type: 'Report' as const, id: r.id })),
              { type: 'ReportList' as const, id: 'LIST' },
            ]
          : [{ type: 'ReportList' as const, id: 'LIST' }],
    }),

    /**
     * LIVE: GET /insight/v1/reports/{id}/runs?page=1&pageSize=1[&merchantId=<id>]
     * Each list card fetches its latest run (pageSize=1) for the status chip;
     * the builder edit view fetches pageSize=3 (Phase 2). Response top-level
     * keys observed live: { runs: Run[], total: number }. (#3)
     */
    getReportRuns: builder.query<RunListResponse, ReportRunsParams>({
      query: (params) => ({ url: buildReportRunsUrl(params), method: 'GET' }),
      transformResponse: (response: RunListResponse): RunListResponse => ({
        runs: response.runs ?? [],
        total: response.total ?? 0,
      }),
      providesTags: (_result, _error, { reportId }) => [{ type: 'RunList', id: reportId }],
    }),

    /**
     * LIVE: GET /insight/v1/reports/runs?page=1&pageSize=50&sortBy=createdAt[&merchantId=<id>]
     * Activity Log (all runs across all reports). Legacy default is
     * pageSize=50 with sortBy=createdAt and NO `sortAsc` param (only appended
     * when truthy). Response top-level keys observed live:
     * { runs: Run[], total: number }. (#5)
     */
    getAllRuns: builder.query<RunListResponse, AllRunsParams>({
      query: (params) => ({ url: buildAllRunsUrl(params), method: 'GET' }),
      transformResponse: (response: RunListResponse): RunListResponse => ({
        runs: response.runs ?? [],
        total: response.total ?? 0,
      }),
      providesTags: [{ type: 'RunList', id: 'ALL' }],
    }),

    /**
     * LIVE: DELETE /insight/v1/reports/{id} → 204 No Content.
     * No scope param (legacy `deleteExport` does not opt into
     * injectEntityOverride). Invalidates the reports list (refetch page 1) AND
     * the deleted report's per-id tag so a cached getReport detail can't linger
     * stale after deletion. (#12)
     */
    deleteReport: builder.mutation<void, string>({
      query: (reportId) => ({ url: buildDeleteReportUrl(reportId), method: 'DELETE' }),
      invalidatesTags: (_result, _error, reportId) => [
        { type: 'ReportList', id: 'LIST' },
        { type: 'Report', id: reportId },
      ],
    }),

    /**
     * LIVE: GET /insight/v1/reports/{id}
     * Builder edit-mode mount. Response is WRAPPED `{ report: Export }` (live,
     * via the legacy saga's `resp.reports[0].report` unwrap) — unwrap
     * explicitly. No scope param (legacy `getSpecificExport` doesn't opt into
     * injectEntityOverride). (#2)
     */
    getReport: builder.query<Export, string>({
      query: (reportId) => ({ url: buildReportUrl(reportId), method: 'GET' }),
      transformResponse: (response: { report: Export }): Export => response.report,
      providesTags: (_result, _error, reportId) => [{ type: 'Report', id: reportId }],
    }),

    /**
     * LIVE: GET /insight/v1/reports/columns?runAs=admin|merchant|vendor
     * Builder mount — the column catalog for the tree. Response top-level key
     * observed live: { columns: Column[] }. (#6)
     */
    getColumns: builder.query<Column[], ScopeKind>({
      query: (runAs) => ({ url: buildColumnsUrl(runAs), method: 'GET' }),
      transformResponse: (response: ColumnsResponse): Column[] => response.columns ?? [],
    }),

    /**
     * LIVE: GET /insight/v1/reports/{id}/runs/{runId}
     * Run-status polling target. Response WRAPPED `{ run: Run }` — unwrap.
     * Not cached so a finished poll doesn't pin a stale status. (#7)
     */
    getRun: builder.query<Run, RunDetailParams>({
      query: (params) => ({ url: buildRunDetailUrl(params), method: 'GET' }),
      transformResponse: (response: { run: Run }): Run => response.run,
      keepUnusedDataFor: 0,
      providesTags: (_result, _error, { runId }) => [{ type: 'Run', id: runId }],
    }),

    /**
     * LIVE: POST /insight/v1/reports
     * Body (link-graph §11): { name, columns, allowForMerchantIds,
     * allowForVendorIds, allowForUserTypes, filter } — NO runAs. Returns
     * `{ id }` (201). The legacy saga auto-runs after create; the builder
     * orchestrates create → run with the returned id (it does NOT live in the
     * mutation, so the two wire calls stay observable). (#8)
     */
    createReport: builder.mutation<{ id: string }, ReportWriteBody>({
      query: (body) => ({ url: `${services.insight}/v1/reports`, method: 'POST', body }),
      invalidatesTags: [{ type: 'ReportList', id: 'LIST' }],
    }),

    /**
     * LIVE: PUT /insight/v1/reports/{id}
     * Same body as create. The saga auto-runs after update too — the builder
     * orchestrates update → run. (#9)
     */
    updateReport: builder.mutation<{ id?: string }, { reportId: string; body: ReportWriteBody }>({
      query: ({ reportId, body }) => ({ url: buildReportUrl(reportId), method: 'PUT', body }),
      invalidatesTags: (_result, _error, { reportId }) => [
        { type: 'Report', id: reportId },
        { type: 'ReportList', id: 'LIST' },
      ],
    }),

    /**
     * LIVE: POST /insight/v1/reports/{id}/run
     * Body (link-graph §11): { merchantIds, vendorIds } (admin sends empty
     * arrays; merchant/vendor send [entityId]). Returns `{ id, reportId }`
     * (201). Invalidates this report's runs + the activity log. (#10)
     */
    runReport: builder.mutation<{ id: string; reportId: string }, RunReportArgs>({
      query: ({ reportId, merchantIds, vendorIds }) => ({
        url: buildRunReportUrl(reportId),
        method: 'POST',
        body: { merchantIds, vendorIds },
      }),
      invalidatesTags: (_result, _error, { reportId }) => [
        { type: 'RunList', id: reportId },
        { type: 'RunList', id: 'ALL' },
      ],
    }),

    /**
     * LIVE: POST /insight/v1/reports/preview
     * Body (link-graph §11): { columns, runAs, limit: 5, filter, merchantIds?,
     * vendorIds? }. Response is `{ data: rows }` (NOT `{ rows }`) — unwrap
     * explicitly. Not cached; driven by a debounced lazy trigger in the
     * builder. (#11)
     */
    preview: builder.query<PreviewResponse, PreviewBody>({
      query: (body) => ({ url: buildPreviewUrl(), method: 'POST', body }),
      transformResponse: (response: PreviewResponse): PreviewResponse => ({
        data: response.data ?? [],
      }),
      keepUnusedDataFor: 0,
    }),

    /**
     * GET /insight/v1/reports/data-refreshed-at
     * When the warehouse behind every export was last loaded. It reloads a few
     * times a day at most, so the value is held long rather than re-fetched.
     */
    getDataRefreshedAt: builder.query<string | null, void>({
      query: () => ({ url: buildDataRefreshedAtUrl(), method: 'GET' }),
      transformResponse: (response: DataRefreshedAtResponse): string | null =>
        response.refreshedAt ?? null,
      keepUnusedDataFor: 300,
    }),
  }),
});

export const {
  useGetReportsQuery,
  useGetReportRunsQuery,
  useGetAllRunsQuery,
  useDeleteReportMutation,
  useGetReportQuery,
  useGetColumnsQuery,
  useGetRunQuery,
  useLazyGetRunQuery,
  useCreateReportMutation,
  useUpdateReportMutation,
  useRunReportMutation,
  useLazyPreviewQuery,
  useGetDataRefreshedAtQuery,
} = exportsApi;
